package com.neurobs.js.ccab

import com.neurobs.ccab.ObjectResult
import com.neurobs.ccab.db.{LanguageData, PhraseData, PhraseGroupData, ProductVersionData, TranslationData, TranslationEngineData, TranslatorData}
import com.neurobs.ccab.translation.{CCABDiff, CCABDiffData, CCABVersionCreateData, OrderedTranslationPhraseData, PhraseDataView, ProductVersionTranslationSearchData, TranslationRequestData, TranslationResponseData, TranslatorDataView, TranslatorProgressData, UploadCCAB}
import com.neurobs.js.http
import com.neurobs.util.JsonPrinter.jsonPrint
import io.circe.generic.auto._

object TranslationServer {

  def getAllLanguages(onComplete: http.ServerObjectResponse[ObjectResult[Seq[LanguageData]]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[Seq[LanguageData]]](
      url = "/api/v1/languages",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getProductVersions(productId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[ProductVersionData]]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("product_id", Some(productId))))
    http.getServerObjectAsync[ObjectResult[Seq[ProductVersionData]]](
      url = s"/api/v1/product_versions$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getAllTranslators(onComplete: http.ServerObjectResponse[ObjectResult[Seq[TranslatorDataView]]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[Seq[TranslatorDataView]]](
      url = "/api/v1/translators",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getTranslatorsForUser(userId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[TranslatorDataView]]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("user_id", Some(userId))))
    http.getServerObjectAsync[ObjectResult[Seq[TranslatorDataView]]](
      url = s"/api/v1/translators/user$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def createTranslator(data: TranslatorData)(onComplete: http.ServerObjectResponse[ObjectResult[TranslatorData]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[TranslatorData]](
      url = s"/api/v1/translators",
      t = http.HTTPRequestType.Post,
      data = jsonPrint(data)
    )(onComplete)
  }

  def getTranslatorProgress(translatorId: Long, productVersionId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[TranslatorProgressData]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("product_version_id", Some(productVersionId))))
    http.getServerObjectAsync[ObjectResult[TranslatorProgressData]](
      url = s"/api/v1/translators/$translatorId/progress$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getPhrases(productVersionId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[PhraseDataView]]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("product_version_id", Some(productVersionId))))
    http.getServerObjectAsync[ObjectResult[Seq[PhraseDataView]]](
      url = s"/api/v1/phrases$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getPhraseGroups(productVersionId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[PhraseGroupData]]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("product_version_id", Some(productVersionId))))
    http.getServerObjectAsync[ObjectResult[Seq[PhraseGroupData]]](
      url = s"/api/v1/phrase_groups$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getPhrasesForGroup(phraseGroupId: Long, translatorId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[OrderedTranslationPhraseData]]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(
      ("phrase_group_id", Some(phraseGroupId)), ("translator_id", Some(translatorId))
    ))
    http.getServerObjectAsync[ObjectResult[Seq[OrderedTranslationPhraseData]]](
      url = s"/api/v1/phrases/translator$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def createCCABVersion(ccabVersionCreateData: CCABVersionCreateData)(onComplete: http.ServerObjectResponse[ObjectResult[ProductVersionData]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[ProductVersionData]](
      url = "/api/v1/product_versions/ccab",
      t = http.HTTPRequestType.Post,
      data = jsonPrint(ccabVersionCreateData)
    )(onComplete)
  }

  def getAllEngines(onComplete: http.ServerObjectResponse[ObjectResult[Seq[TranslationEngineData]]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[Seq[TranslationEngineData]]](
      url = "/api/v1/translation_engines",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getUserPhraseTranslation(phraseId: Long, translatorId: Long)(onComplete: http.ServerObjectResponse[Option[TranslationData]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("phrase_id", Some(phraseId)), ("translator_id", Some(translatorId))))
    http.getServerObjectAsync[Option[TranslationData]](
      url = s"/api/v1/translations/user$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getEnginePhraseTranslation(phraseId: Long, engineId: Long, languageId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[TranslationData]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("phrase_id", Some(phraseId)), ("engine_id", Some(engineId)), ("language_id", Some(languageId))))
    http.getServerObjectAsync[ObjectResult[TranslationData]](
      url = s"/api/v1/translations/engine$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def updateUserTranslation(translationData: TranslationData)(onComplete: http.ServerObjectResponse[ObjectResult[TranslationData]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[TranslationData]](
      url = "/api/v1/translations",
      t = http.HTTPRequestType.Put,
      data = jsonPrint(translationData)
    )(onComplete)
  }

  def createUserTranslation(translationData: TranslationData)(onComplete: http.ServerObjectResponse[ObjectResult[TranslationData]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[TranslationData]](
      url = "/api/v1/translations",
      t = http.HTTPRequestType.Post,
      data = jsonPrint(translationData)
    )(onComplete)
  }

  def getTextTranslation(translationRequestData: TranslationRequestData)(onComplete: http.ServerObjectResponse[ObjectResult[TranslationResponseData]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[TranslationResponseData]](
      url = "/api/v1/translations/translate",
      t = http.HTTPRequestType.Post,
      data = jsonPrint(translationRequestData)
    )(onComplete)
  }

  def getTranslationReport(phraseGroupId: Long, translatorId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Array[Byte]]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("phrase_group_id", Some(phraseGroupId)), ("translator_id", Some(translatorId))))
    http.getServerObjectAsync[ObjectResult[Array[Byte]]](
      url = s"/api/v1/translations/report$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getProductVersionTranslations(productVersionId: Long, translatorId: Long)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[OrderedTranslationPhraseData]]] => Unit) = {
    val suffix = CCABServer.makeUrlSuffix(Seq(("translator_id", Some(translatorId))))
    http.getServerObjectAsync[ObjectResult[Seq[OrderedTranslationPhraseData]]](
      url = s"/api/v1/product_versions/$productVersionId/translations$suffix",
      t = http.HTTPRequestType.Get
    )(onComplete)
  }

  def getCCABDiff(productVersionId: Long, uploadCCAB: UploadCCAB)(onComplete: http.ServerObjectResponse[ObjectResult[CCABDiffData]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[CCABDiffData]](
      url = s"/api/v1/phrases/ccab/$productVersionId/diff",
      t = http.HTTPRequestType.Post,
      data = jsonPrint(uploadCCAB)
    )(onComplete)
  }

  def searchUserTranslationsBySubPhrase(productVersionId: Long, translatorId: Long, subPhrase: String)(onComplete: http.ServerObjectResponse[ObjectResult[Seq[TranslationData]]] => Unit) = {
    http.getServerObjectAsync[ObjectResult[Seq[TranslationData]]](
      url = s"/api/v1/product_versions/$productVersionId/translations/subphrase",
      t = http.HTTPRequestType.Post,
      data = jsonPrint(ProductVersionTranslationSearchData(productVersionId, translatorId, subPhrase))
    )(onComplete)
  }
}
